import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Dropdown } from 'primereact/dropdown';
import Cookies from 'js-cookie';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import iconLocales from '../assets/icons/locations_icon.svg';
import iconLocalesBlack from '../assets/icons/locations_icon_black.svg';
import TopHeaderLocalesDropDownTemplate from './TopHeaderLocalesDropDownTemplate';
import useWindowSize from '../hooks/useWindowSize';
import DropDownIconTemplate from './DropDownIconTemplate';
import { locales } from '../utils/dropDownItems';
import callIcon from '../assets/icons/phone_icon.svg';
import callToggle from '../store/actions/callToggle';
import searchIcon from '../assets/icons/searchIcon.svg';
import Api from '../api';
import Spinner from './Spinner';
import { socialLinks } from '../utils/data/link/data';
import rateUsIcon from '../assets/icons/rate-us-icon.svg';

const rateUsLinks = {
  am: 'https://b24-p4lkbt.bitrix24.site/crm_form_e67nm/ ',
  ru: 'https://b24-p4lkbt.bitrix24.site/crm_form_cfdyi/ ',
  en: 'https://b24-p4lkbt.bitrix24.site/crm_form_znhfj/',
};
function DropdownItemsTemplate({ item, isLast }) {
  return (
    <div className="dropdown-item">
      <div className={`dropdown-item-icon-block ${item.icon ? 'languages' : ''}`}>
        <img src={item.icon ? item.icon : iconLocalesBlack} alt="" />
        <p className="dropdown-item-text">{item.name}</p>
      </div>
      <span className={`dropdown-item-bottom ${isLast ? 'last' : ''}`} />
      {/* <div className="dropdown-item-hover-overlay" /> */}
    </div>
  );
}

function TopHeader({ data }) {
  const { width } = useWindowSize();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.translation);
  const lang = Cookies.get('i18next') || 'en';
  const [selectedCity, setSelectedCity] = useState(JSON.parse(localStorage.getItem('city')) || null);
  const [selectedLocale, setSelectedLocale] = useState(locales.find((e) => e.code === lang));
  const [suggestion, setSuggestion] = useState([]);
  const [noSuggests, setNoSuggests] = useState(false);
  const [query, setQuery] = useState('');
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  const containerRef = useRef(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const cities = useMemo(() => data?.map((e) => ({
    name: e.address[lang],
    code: e.address[lang],
    id: e.id,
  })), [data]);
  useEffect(() => {
    const handler = setTimeout(async () => {
      setNoSuggests(false);
      if (query) {
        setIsSuggestionsVisible(true);
        setSearchLoading(true);
        const { data: response } = await Api.getSearchResults(query);
        setSearchLoading(false);
        setSuggestion(response.data);
        if (response.data.length === 0) {
          setNoSuggests(true);
        }
      } else {
        setIsSuggestionsVisible(false);
      }
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [query]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsSuggestionsVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const suggestionsVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
  };
  const handleCloseModal = () => {
    dispatch(callToggle({ visible: true, isCall: true, item: null }));
  };
  const highlightMatch = (text) => {
    if (!query) return text;
    if (!text) return null;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) => (regex.test(part) ? (
    // eslint-disable-next-line react/no-array-index-key
      <span key={i} style={{ color: '#005AA8' }}>
        {part}
      </span>
    ) : (
      part
    )));
  };
  const handleLanguageChange = (language, e) => {
    i18next.changeLanguage(language).then(() => {
      Cookies.set('i18next', language);
      window.dispatchEvent(new Event('languageChanged'));
      const currentPath = window.location.pathname;
      if (currentPath.includes('/section-description/')) {
        setSelectedLocale(e.value);
        setTimeout(() => {
          window.location.reload();
        }, 50);
        return;
      }
      const languagePrefix = currentPath.split('/')[1];
      let newPath;
      if (languagePrefix && languagePrefix.length === 2) {
        newPath = currentPath.replace(`/${languagePrefix}`, `/${language}`);
      } else {
        newPath = `/${language}${currentPath}`;
      }
      window.location.href = newPath;
      setSelectedLocale(e.value);
    });
  };
  return (
    <div className="top-header">
      <div className="container">
        <div className="row">
          <div className="top-header__links">
            <ul className="top-header__links-list">
              {socialLinks.map((link) => (
                <li key={link.title} className="top-header__link-item">
                  <a
                    href={link.href}
                    className="top-header__link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={link.icon} alt={link.title} className="top-header__icon" />
                  </a>
                </li>
              ))}
              {width > 768 && (
                <li className="rate-us-button">
                  <a
                    href={rateUsLinks[lang]}
                    className="top-header__link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src={rateUsIcon} alt="rate us" className="top-header__icon" />
                    <p>RATE US!</p>
                  </a>
                </li>
              )}
            </ul>
          </div>
          <div className="top-header__locales">
            {width > 768 && (
            <div className="search-bar-container" ref={containerRef}>
              <div className="search-bar">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  type="text"
                  className="search-bar-input"
                  placeholder={translations?.search_input?.[lang]}
                />
                <img src={searchIcon} alt="" className="search-bar-icon" />
              </div>
              {isSuggestionsVisible && (
              <motion.div
                className="search-bar-suggestions"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={suggestionsVariants}
              >
                <ul>
                  {searchLoading && <Spinner isSearch size={50} width={50} height={50} />}
                  {noSuggests && <li className="suggestion-item">{t('no_suggestion')}</li>}
                  {searchLoading || suggestion.map((item) => (
                    <li
                      role="presentation"
                      onClick={() => {
                        if (item.model_type === 'Address') {
                          navigate(`/${lang}/addresses?address=${item.id}`);
                        } else {
                          window.location.assign(`/${lang}/section-description/${item.slug[lang]}`);
                        }
                      }}
                      className="suggestion-item"
                      key={item.id + item.model_type}
                    >
                      {highlightMatch(item.model_type === 'Address' ? item.address[lang] : item.title[lang], query)}
                    </li>
                  ))}
                </ul>
              </motion.div>
              )}
            </div>
            )}
            {width > 768 && (
              <div className="top-header__phone">
                <a target="_blank" href="https://t.me/EcoSene_bot" rel="noreferrer" className="top-header__phone-item">
                  <p>{t('telegram_bot')}</p>
                </a>
              </div>
            )}
            <div className="top-header__phone">
              <a href="tel:8676" className="top-header__phone-item">
                <img src={callIcon} alt="telephone" />
                <p>8676</p>
              </a>
            </div>
            {width > 768 && (
            <div
              role="presentation"
              onClick={handleCloseModal}
              className="header__actions-telegram call-btn top-btn"
            >
              <img src={callIcon} alt="callIcon" />
              <p>{t('go_home')}</p>
            </div>
            )}
            {width > 768 && (
            <>
              <div className="top-header__locales_locales after">
                <Dropdown
                  panelClassName="locations-dropdown-panel"
                  className="locations-dropdown"
                  dropdownIcon={() => <DropDownIconTemplate collapse={false} />}
                  collapseIcon={() => <DropDownIconTemplate collapse />}
                  valueTemplate={() => (
                    <TopHeaderLocalesDropDownTemplate
                      icon={iconLocales}
                      title={selectedCity ? selectedCity.name : t('locations')}
                    />
                  )}
                  itemTemplate={(element) => (
                    <DropdownItemsTemplate
                      item={element}
                      isLast={element.name === cities[cities.length - 1].name}
                    />
                  )}
                  value={selectedCity}
                  onChange={(e) => {
                    localStorage.setItem('city', JSON.stringify(e.value));
                    setSelectedCity(e.value);
                    navigate(`/${lang}/addresses?address=${e.value.id}`);
                  }}
                  options={cities}
                  optionLabel="ывыв"
                  placeholder="Select a Locations"
                />
              </div>

              <div className="top-header__locales_after" />
            </>
            )}
            <div className="top-header__locales_locales">
              <Dropdown
                panelClassName="locations-dropdown-panel"
                className="locations-dropdown languages"
                dropdownIcon={() => <DropDownIconTemplate collapse={false} />}
                collapseIcon={() => <DropDownIconTemplate collapse />}
                valueTemplate={() => (
                  <TopHeaderLocalesDropDownTemplate
                    isLanguage
                    icon={selectedLocale.icon}
                    title={selectedLocale?.name}
                  />
                )}
                itemTemplate={(element) => (
                  <DropdownItemsTemplate
                    item={element}
                    isLast={element.name === locales[locales.length - 1].name}
                  />
                )}
                value={selectedLocale}
                onChange={(e) => {
                  handleLanguageChange(e.value.code, e);
                }}
                options={locales.filter((e) => e.code !== lang)}
                optionLabel="name"
                placeholder="Select a Locations"
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default TopHeader;
